import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tag, Plus, Pencil, Trash2, BarChart3, Eye, EyeOff, Users, Calendar } from "lucide-react";

export default function AdminPromotions() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    discount_type: "percentage",
    discount_value: 20,
    duration_type: "months",
    duration_value: 3,
    applicable_plans: ["starter", "pro", "advanced"],
    billing_cycles: ["monthly", "annual"],
    max_redemptions: null,
    start_date: "",
    end_date: "",
    is_active: true,
    requires_code: false,
    code: "",
    stackable: false,
    visibility: "public",
  });

  const token = localStorage.getItem("admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPromotions();
  }, [includeInactive]);

  const fetchPromotions = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/promotions?include_inactive=${includeInactive}`,
        { headers }
      );
      setPromotions(response.data);
    } catch (error) {
      toast.error("Failed to load promotions");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : new Date().toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        max_redemptions: formData.max_redemptions || null,
        duration_value: formData.duration_type === "months" ? formData.duration_value : null,
      };

      await axios.post(`${API}/admin/promotions`, payload, { headers });
      toast.success("Promotion created successfully");
      setShowCreateModal(false);
      resetForm();
      fetchPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promotion");
    }
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : undefined,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        max_redemptions: formData.max_redemptions || null,
      };

      await axios.put(`${API}/admin/promotions/${selectedPromo.id}`, payload, { headers });
      toast.success("Promotion updated successfully");
      setShowEditModal(false);
      fetchPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update promotion");
    }
  };

  const handleToggleActive = async (promo) => {
    try {
      const endpoint = promo.is_active ? "deactivate" : "activate";
      await axios.post(`${API}/admin/promotions/${promo.id}/${endpoint}`, {}, { headers });
      toast.success(`Promotion ${promo.is_active ? "deactivated" : "activated"}`);
      fetchPromotions();
    } catch (error) {
      toast.error("Failed to update promotion status");
    }
  };

  const handleViewAnalytics = async (promo) => {
    try {
      const response = await axios.get(`${API}/admin/promotions/${promo.id}/analytics`, { headers });
      setAnalytics(response.data);
      setSelectedPromo(promo);
      setShowAnalyticsModal(true);
    } catch (error) {
      toast.error("Failed to load analytics");
    }
  };

  const openEditModal = (promo) => {
    setSelectedPromo(promo);
    setFormData({
      name: promo.name,
      description: promo.description || "",
      discount_type: promo.discount_type,
      discount_value: promo.discount_value,
      duration_type: promo.duration_type,
      duration_value: promo.duration_value || 3,
      applicable_plans: promo.applicable_plans,
      billing_cycles: promo.billing_cycles,
      max_redemptions: promo.max_redemptions,
      start_date: promo.start_date ? promo.start_date.split("T")[0] : "",
      end_date: promo.end_date ? promo.end_date.split("T")[0] : "",
      is_active: promo.is_active,
      requires_code: promo.requires_code,
      code: "",
      stackable: promo.stackable,
      visibility: promo.visibility,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      discount_type: "percentage",
      discount_value: 20,
      duration_type: "months",
      duration_value: 3,
      applicable_plans: ["starter", "pro", "advanced"],
      billing_cycles: ["monthly", "annual"],
      max_redemptions: null,
      start_date: "",
      end_date: "",
      is_active: true,
      requires_code: false,
      code: "",
      stackable: false,
      visibility: "public",
    });
  };

  const getVisibilityBadge = (visibility) => {
    const colors = {
      public: "bg-green-500/20 text-green-400",
      founding: "bg-purple-500/20 text-purple-400",
      enterprise: "bg-blue-500/20 text-blue-400",
      internal: "bg-gray-500/20 text-gray-400",
    };
    return <Badge className={colors[visibility]}>{visibility}</Badge>;
  };

  const getDiscountDisplay = (promo) => {
    if (promo.discount_type === "percentage") {
      return `${promo.discount_value}% off`;
    }
    return `$${promo.discount_value} off`;
  };

  const getDurationDisplay = (promo) => {
    if (promo.duration_type === "once") return "First payment only";
    if (promo.duration_type === "forever") return "Locked forever";
    return `${promo.duration_value} months`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-promotions">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Tag className="w-6 h-6" />
            Promotions & Discounts
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage promotional campaigns, discounts, and special offers
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="create-promotion-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Promotion
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={includeInactive}
            onCheckedChange={setIncludeInactive}
            id="include-inactive"
          />
          <Label htmlFor="include-inactive" className="text-sm text-muted-foreground">
            Show inactive promotions
          </Label>
        </div>
      </div>

      {/* Promotions Grid */}
      <div className="grid gap-4">
        {promotions.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <Tag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No promotions yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first promotion to offer discounts to your users
            </p>
            <Button onClick={() => { resetForm(); setShowCreateModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Create Promotion
            </Button>
          </div>
        ) : (
          promotions.map((promo) => (
            <div
              key={promo.id}
              className={`bg-card border rounded-lg p-6 ${
                promo.is_active ? "border-border" : "border-border/50 opacity-60"
              }`}
              data-testid={`promotion-card-${promo.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">{promo.name}</h3>
                    {getVisibilityBadge(promo.visibility)}
                    {!promo.is_active && (
                      <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>
                    )}
                    {promo.has_code && (
                      <Badge variant="outline" className="text-yellow-500 border-yellow-500/30">
                        Code Required
                      </Badge>
                    )}
                  </div>
                  {promo.description && (
                    <p className="text-muted-foreground text-sm mb-3">{promo.description}</p>
                  )}
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Discount:</span>
                      <p className="font-medium text-foreground">{getDiscountDisplay(promo)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Duration:</span>
                      <p className="font-medium text-foreground">{getDurationDisplay(promo)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Redemptions:</span>
                      <p className="font-medium text-foreground">
                        {promo.current_redemptions}
                        {promo.max_redemptions && ` / ${promo.max_redemptions}`}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Plans:</span>
                      <p className="font-medium text-foreground capitalize">
                        {promo.applicable_plans.join(", ")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(promo.start_date).toLocaleDateString()}
                      {promo.end_date && ` - ${new Date(promo.end_date).toLocaleDateString()}`}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleViewAnalytics(promo)}
                    data-testid={`analytics-btn-${promo.id}`}
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(promo)}
                    data-testid={`edit-btn-${promo.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(promo)}
                    data-testid={`toggle-btn-${promo.id}`}
                  >
                    {promo.is_active ? (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Eye className="w-4 h-4 text-green-500" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={() => { setShowCreateModal(false); setShowEditModal(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditModal ? "Edit Promotion" : "Create Promotion"}</DialogTitle>
            <DialogDescription>
              {showEditModal ? "Update promotion details" : "Create a new promotional offer"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Promotion Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Cyber Monday 2024"
                  data-testid="promo-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Visibility</Label>
                <Select
                  value={formData.visibility}
                  onValueChange={(value) => setFormData({ ...formData, visibility: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="founding">Founding Members</SelectItem>
                    <SelectItem value="enterprise">Enterprise Only</SelectItem>
                    <SelectItem value="internal">Internal/Testing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the promotion"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Discount Value *</Label>
                <Input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                  placeholder={formData.discount_type === "percentage" ? "e.g., 20" : "e.g., 10"}
                  data-testid="promo-discount-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duration Type</Label>
                <Select
                  value={formData.duration_type}
                  onValueChange={(value) => setFormData({ ...formData, duration_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">One-time (first payment)</SelectItem>
                    <SelectItem value="months">Time-limited (months)</SelectItem>
                    <SelectItem value="forever">Locked forever</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.duration_type === "months" && (
                <div className="space-y-2">
                  <Label>Number of Months</Label>
                  <Input
                    type="number"
                    value={formData.duration_value}
                    onChange={(e) => setFormData({ ...formData, duration_value: parseInt(e.target.value) })}
                    min={1}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="promo-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  data-testid="promo-end-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Max Redemptions (leave empty for unlimited)</Label>
              <Input
                type="number"
                value={formData.max_redemptions || ""}
                onChange={(e) => setFormData({ ...formData, max_redemptions: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="Unlimited"
              />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.requires_code}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_code: checked })}
                  id="requires-code"
                />
                <Label htmlFor="requires-code">Requires promo code</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.stackable}
                  onCheckedChange={(checked) => setFormData({ ...formData, stackable: checked })}
                  id="stackable"
                />
                <Label htmlFor="stackable">Stackable with other promos</Label>
              </div>
            </div>

            {formData.requires_code && (
              <div className="space-y-2">
                <Label>Promo Code {showEditModal && "(leave empty to keep existing)"}</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., CYBER2024"
                  data-testid="promo-code-input"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}>
              Cancel
            </Button>
            <Button onClick={showEditModal ? handleUpdate : handleCreate} data-testid="save-promotion-btn">
              {showEditModal ? "Update Promotion" : "Create Promotion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analytics Modal */}
      <Dialog open={showAnalyticsModal} onOpenChange={setShowAnalyticsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Promotion Analytics</DialogTitle>
            <DialogDescription>{selectedPromo?.name}</DialogDescription>
          </DialogHeader>

          {analytics && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-muted-foreground text-sm">Total Redemptions</p>
                <p className="text-2xl font-bold text-foreground">{analytics.total_redemptions}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-muted-foreground text-sm">Active</p>
                <p className="text-2xl font-bold text-green-500">{analytics.active_redemptions}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-muted-foreground text-sm">Expired</p>
                <p className="text-2xl font-bold text-yellow-500">{analytics.expired_redemptions}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-muted-foreground text-sm">Total Discount Given</p>
                <p className="text-2xl font-bold text-foreground">${analytics.total_discount_given}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnalyticsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
